<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    :class="`relative @container text-foreground ${hasFocus ? 'z-100' : ''}`"
    @focusin="hasFocus = true"
    @focusout="
      (e) => {
        if (!pendingResultClick && !e.currentTarget.contains(e.relatedTarget)) hasFocus = false;
      }
    ">
    <Transition name="fade">
      <div v-if="hasFocus" class="fixed z-1 inset-0 bg-black/50" @click="close"></div>
    </Transition>

    <!-- Search input bar -->
    <div class="relative z-10 flex items-stretch group">
      <div
        :class="`flex flex-1 items-center overflow-hidden border border-gray-200 bg-surface hover:border-gray-400 transition h-11 ${hasFocus && query ? 'rounded-t-lg' : 'rounded-lg'}`">
        <div class="flex-none size-11 flex items-center justify-center group-hover:scale-105 transition-transform">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.5">
            <circle cx="11" cy="11" r="7" />
            <line x1="16.5" y1="16.5" x2="22" y2="22" />
          </svg>
        </div>
        <input
          v-model="query"
          name="search"
          type="text"
          maxlength="75"
          autocorrect="off"
          spellcheck="false"
          autocomplete="off"
          :aria-label="t('Search')"
          :placeholder="t('Find your next event here')"
          class="flex-1 text-base px-0 placeholder-gray-400 outline-none border-none ring-0 focus:ring-0 focus:outline-none appearance-none bg-transparent" />
        <CancelIcon
          v-if="query"
          class="size-4 cursor-pointer text-muted-foreground flex-none mr-3 hover:text-[#991B1B] hover:scale-105 transition"
          @click="close" />
      </div>
    </div>

    <!-- Search results dropdown -->
    <div
      v-show="query && hasFocus"
      ref="resultsWrapperElement"
      class="absolute bg-surface w-full search-results inset-x-0 pb-6 rounded-b-2xl border-gray-200 flex flex-col gap-3 z-10"
      @mousedown.prevent
      @touchstart="pendingResultClick = true"
      @touchend="pendingResultClick = false">
      <div class="px-6 flex flex-col gap-3">
        <template v-if="!isLoading">
          <template v-if="otherResults.length">
            <RouterLink
              v-for="result in otherResults"
              :key="result.to"
              :to="result.to"
              class="flex gap-1 items-center"
              :class="events.length ? `mt-2` : ``">
              <component :is="result.icon" class="size-4 opacity-50" />
              <span class="me-1" v-html="highlightQuery(result.name)"></span>
              <span v-if="isTeamResult(result)" class="bg-background text-xs font-medium me-1 px-2.5 py-0.5 rounded-sm">
                {{ result.sportType }}
              </span>
              <span class="bg-background text-xs font-medium me-1 px-2.5 py-0.5 rounded-sm">{{ result.type }}</span>
            </RouterLink>
          </template>
          <hr v-if="events.length" />
          <template v-if="events.length">
            <span class="text-sm text-muted-foreground font-bold @xl:my-3">{{ t("Upcoming events") }}</span>
            <template v-for="(event, i) in events" :key="event.eventId">
              <EventCard :event="camelToSnake(event)" class="p-0! shadow-none" small />
              <hr v-if="i < events.length - 1" class="@xl:my-3" />
            </template>
          </template>
          <template v-if="!otherResults.length && !events.length">
            <span class="mt-2">{{ $t("No results found") }}</span>
          </template>
        </template>
        <template v-else>
          <div class="flex flex-col gap-2 animate-pulse">
            <div v-for="n in 4" :key="n" class="bg-accent rounded h-12"></div>
            <div v-for="n in 4" :key="`s${n}`" class="bg-accent rounded h-4"></div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useTeams, useCities, useVenues, useTournaments, useEventsSearch } from "@xs2event/js-lib/api";
import { RouterLink } from "vue-router";
import { todayISODate } from "@/helpers/format";
import { EventCard } from "@/components/event";
import { camelToSnake, snakeToCamel } from "@/helpers/casing";
import brandconfig from "@/brandconfig";
import CancelIcon from "~icons/ic/baseline-cancel";
import ApartmentIcon from "~icons/ic/baseline-apartment";
import EmojiEventsIcon from "~icons/ic/baseline-emoji-events";
import StadiumIcon from "~icons/ic/baseline-stadium";
import Apparel from "~icons/material-symbols/apparel-sharp";
import { useRoute } from "vue-router";
import { useEventListener, refDebounced } from "@vueuse/core";
import { lookupSportTypeName } from "@/store/sportTypes";
import { eventsWithCity, eventsWithTeam, eventsWithTournament, eventsWithVenue } from "@/constants/routes";

const hasFocus = ref(false);
const pendingResultClick = ref(false);
const { t } = useI18n();

const route = useRoute();

watch(
  () => ({ ...route }),
  () => {
    query.value = "";
    hasFocus.value = false;
  },
  { deep: true }
);

useEventListener("keydown", (e: KeyboardEvent) => {
  if (!hasFocus.value) return;

  if (e.key === "Escape") {
    close();
  }
});

const close = () => {
  (document.activeElement as HTMLElement)?.blur();
  hasFocus.value = false;
  query.value = "";
};

const filterIcons = {
  city: ApartmentIcon,
  team: Apparel,
  tournament: EmojiEventsIcon,
  venue: StadiumIcon,
} as const;

const search = snakeToCamel(brandconfig.search);

const query = ref<string>("");
const debouncedQuery = refDebounced(query, 400);

const isLoading = computed(() => {
  return (
    teamsQuery.isLoading.value ||
    citiesQuery.isLoading.value ||
    tournamentsQuery.isLoading.value ||
    venuesQuery.isLoading.value ||
    eventsQuery.isLoading.value
  );
});

const teamsQuery = useTeams(
  computed(() => ({
    teamName: (debouncedQuery.value && `ilike:${debouncedQuery.value}`) || undefined,
    sorting: "team_name:asc",
    pageSize: 5,
    eventStartdate: `ge:${new Date().toISOString().split("T")[0]}`,
    ...search.params,
    ...search.teams.params,
  })),
  { enabled: computed(() => !!debouncedQuery.value), debounce: false }
);

const citiesQuery = useCities(
  computed(() => ({ query: (debouncedQuery.value && `ilike:${debouncedQuery.value}`) || undefined, pageSize: 5 })),
  { enabled: computed(() => !!debouncedQuery.value), debounce: false }
);

const tournamentsQuery = useTournaments(
  computed(() => ({
    tournamentName: (debouncedQuery.value && `ilike:${debouncedQuery.value}`) || undefined,
    dateStop: "ge:" + todayISODate,
    sorting: "tournament_name:asc",
    pageSize: 5,
    ...search.params,
    ...search.teams.params,
  })),
  { enabled: computed(() => !!debouncedQuery.value), debounce: false }
);

const venuesQuery = useVenues(
  computed(() => ({
    venueName: (debouncedQuery.value && `ilike:${debouncedQuery.value}`) || undefined,
    pageSize: 5,
    eventStopdate: "ge:" + todayISODate,
    sorting: "official_name:asc",
    ...search.params,
    ...search.teams.params,
  })),
  { enabled: computed(() => !!debouncedQuery.value), debounce: false }
);

const eventsQuery = useEventsSearch(
  computed(() => ({
    query: debouncedQuery.value,
    pageSize: 5,
  })),
  { enabled: computed(() => !!debouncedQuery.value), debounce: false }
);

const events = computed(() => eventsQuery.data.value?.events || []);

const highlightQuery = (text: string) => {
  if (!query.value) return text;
  const escaped = query.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(escaped, "gi");

  return text.replace(regex, (match) => `<span class="font-bold">${match}</span>`);
};
const otherResults = computed(() => {
  const results = [
    ...(teamsQuery.data.value?.teams?.map((x) => {
      return {
        type: "team",
        name: `${x.officialName}`,
        sportType: `${t(lookupSportTypeName(x.sportType))}`,
        icon: filterIcons.team,
        to: eventsWithTeam(x.slug.toLowerCase(), { sport_type: x.sportType == "soccer" ? "football" : x.sportType }),
      };
    }) || []),
    ...(venuesQuery.data.value?.venues?.map((x) => ({
      type: "venue",
      name: x.officialName,
      icon: filterIcons.venue,
      to: eventsWithVenue(x.slug.toLowerCase()),
    })) || []),
    ...(citiesQuery.data.value?.cities?.map((x) => ({
      type: "city",
      name: x.city,
      icon: filterIcons.city,
      to: eventsWithCity(x.city.toLowerCase().replace(" ", "-")),
    })) || []),
    ...(tournamentsQuery.data.value?.map((x) => ({
      type: "tournament",
      name: `${x.officialName} (${x.season})`,
      icon: filterIcons.tournament,
      to: eventsWithTournament(x.slug.toLowerCase()),
    })) || []),
  ];
  const typeOrder = { team: 0, venue: 1, city: 2, tournament: 3 };
  results.sort((a, b) => {
    if (typeOrder[a.type] !== typeOrder[b.type]) {
      return typeOrder[a.type] - typeOrder[b.type];
    }
    return a.name.localeCompare(b.name);
  });
  return results.slice(0, 5);
});

function isTeamResult(result: OtherResult): result is TeamResult {
  return result.type === "team" && "sportType" in result;
}
</script>

<style scoped>
.search-results {
  max-height: calc(100svh - var(--spacing) * 48);
  overflow: auto;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 150ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
